import { ComponentProps, useContext, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsomorphicLayoutEffect } from '../../../../hooks/utils/useIsomorphicLayoutEffect';
import { useMediaQuery } from '../../../../hooks/utils/useMediaQuery';
import { SActionItem } from '../../s-action-list/SActionButton';
import { SActionList } from '../../s-action-list/SActionList';
import {
    S_INDEX_TABLE_SELECT_ALL_ITEMS,
    SIndexTableContext,
    SIndexTableResourceName,
    SIndexTableSelectionType,
    useSIndexTableSelectionChange,
} from '../index-provider';
import { SIndexTableHeading } from '../types/s-index-table-heading';
import { SIndexTablePagination } from '../types/s-index-table-pagination';

export default function SIndexTableBase(props: SIndexTableBaseProps): React.JSX.Element {
    const _componentId = useId();
    const { t } = useTranslation('polaris');
    const { selectable, selectedItemsCount, itemCount, disableSelection } = useContext(SIndexTableContext);
    const onSelectionChange = useSIndexTableSelectionChange();
    const ref = useRef<HTMLElementTagNameMap['s-table']>(undefined);
    const smDown = useMediaQuery(`only screen and (max-width: 490px)`);

    const selectAllCheckbox = useMemo(
        () => (
            <s-table-header>
                {!props.hideSelectAll ? (
                    <s-checkbox
                        accessibilityLabel={t('Polaris.IndexProvider.a11yCheckboxSelectAllMultiple', {
                            itemsLength:
                                selectedItemsCount === S_INDEX_TABLE_SELECT_ALL_ITEMS ? itemCount : selectedItemsCount,
                            resourceNamePlural: props.resourceName.plural.toLocaleLowerCase(),
                        })}
                        checked={
                            selectedItemsCount === S_INDEX_TABLE_SELECT_ALL_ITEMS ||
                            (!!itemCount && selectedItemsCount === itemCount)
                        }
                        indeterminate={
                            selectedItemsCount !== S_INDEX_TABLE_SELECT_ALL_ITEMS &&
                            selectedItemsCount !== itemCount &&
                            selectedItemsCount > 0
                        }
                        onChange={(_) =>
                            onSelectionChange(
                                SIndexTableSelectionType.All,
                                !(
                                    selectedItemsCount === S_INDEX_TABLE_SELECT_ALL_ITEMS ||
                                    selectedItemsCount === itemCount
                                )
                            )
                        }
                        disabled={!itemCount || disableSelection}
                    />
                ) : undefined}
            </s-table-header>
        ),
        [selectedItemsCount, itemCount, props.resourceName, props.hideSelectAll]
    );

    const bulkActionsButton = useMemo(() => {
        return props.bulkActions?.length ? (
            <>
                <s-button
                    icon="menu-horizontal"
                    accessibilityLabel="Bulk actions"
                    id={`s-index-table-${_componentId}-bulk-actions-activator`}
                    commandFor={`s-index-table-${_componentId}-bulk-actions-popover`}
                />

                <s-popover id={`s-index-table-${_componentId}-bulk-actions-popover`}>
                    <s-box padding="small-300">
                        <SActionList actions={props.bulkActions} />
                    </s-box>
                </s-popover>
            </>
        ) : undefined;
    }, [props.bulkActions]);

    const mappedHeadings = useMemo(
        () => (
            <>
                {props.headings.map((heading, index) => (
                    <s-table-header
                        listSlot={heading?.listSlot ?? (index === 0 ? 'primary' : 'labeled')}
                        key={heading?.id ?? `s-index-table-${_componentId}-heading-${index}`}
                        id={heading?.id ?? `s-index-table-${_componentId}-heading-${index}`}
                        format={heading?.alignment === 'end' ? 'numeric' : undefined}
                    >
                        <s-text
                            interestFor={
                                !!heading?.tooltipContent && !smDown
                                    ? `s-index-table-${_componentId}-heading-${index}-tooltip`
                                    : undefined
                            }
                        >
                            <div
                                style={{
                                    whiteSpace: 'nowrap',
                                    textAlign: heading?.alignment === 'end' ? 'end' : 'start',
                                }}
                            >
                                {heading.title}
                            </div>
                        </s-text>
                        {!!heading?.tooltipContent && !smDown && (
                            <s-tooltip id={`s-index-table-${_componentId}-heading-${index}-tooltip`}>
                                {heading?.tooltipContent}
                            </s-tooltip>
                        )}
                    </s-table-header>
                ))}
            </>
        ),
        [props.headings, smDown]
    );

    const tableHeadings = useMemo(() => {
        return !!itemCount && selectedItemsCount && !props.hideSelectAll ? (
            <>
                {selectAllCheckbox}
                {props.headings.map((heading, index) => (
                    <s-table-header
                        listSlot={index === 0 ? 'primary' : undefined}
                        key={heading?.id ?? `s-index-table-${_componentId}-heading-${index}`}
                        id={heading?.id ?? `s-index-table-${_componentId}-heading-${index}`}
                        format={index === props.headings.length - 1 ? 'numeric' : undefined}
                    >
                        {index === 0
                            ? t('Polaris.IndexProvider.selected', {
                                  selectedItemsCount:
                                      selectedItemsCount === S_INDEX_TABLE_SELECT_ALL_ITEMS
                                          ? itemCount
                                          : selectedItemsCount,
                              })
                            : undefined}
                        {index === props.headings.length - 1 ? bulkActionsButton : undefined}
                    </s-table-header>
                ))}
            </>
        ) : (
            <>
                {selectable ? selectAllCheckbox : undefined}

                {mappedHeadings}
            </>
        );
    }, [mappedHeadings, selectAllCheckbox, itemCount, selectedItemsCount, bulkActionsButton, props.headings]);

    // https://tenor.com/view/rigby-freaky-cat-cat-meme-tongue-out-gif-1492882876567450372
    useIsomorphicLayoutEffect(() => {
        if (!smDown && !!ref.current?.shadowRoot && !!itemCount) {
            setTimeout(() => {
                const table = ref.current?.shadowRoot?.querySelector('.table');
                if (table && table.parentNode && !table.parentElement?.classList.contains('table-scroll-wrapper')) {
                    const wrapper = document.createElement('div');
                    wrapper.classList.add('table-scroll-wrapper');
                    wrapper.style.overflowX = 'auto';
                    table.parentNode.insertBefore(wrapper, table);
                    wrapper.appendChild(table);
                }
            }, 0);
        } else if (smDown && !!ref.current?.shadowRoot) {
            const tableWrapper = ref.current?.shadowRoot?.querySelector('.table-wrapper');
            tableWrapper?.querySelector('.table-scroll-wrapper')?.remove();
        }
    }, [ref.current, itemCount, smDown]);

    return (
        <s-table
            ref={ref}
            id={`s-index-table-${_componentId}`}
            paginate={(!!props?.pagination?.hasNext || !!props?.pagination?.hasPrevious) ?? false}
            hasNextPage={props?.pagination?.hasNext ?? false}
            hasPreviousPage={props?.pagination?.hasPrevious ?? false}
            // @ts-expect-error funny shopify types
            onnextpage={props?.pagination?.onNext ?? undefined}
            onpreviouspage={props?.pagination?.onPrevious ?? undefined}
            loading={props?.loading ?? false}
            variant={props?.condensed ? 'list' : 'auto'}
        >
            <s-table-header-row>{tableHeadings}</s-table-header-row>

            <s-table-body>{props.children}</s-table-body>
        </s-table>
    );
}

export interface SIndexTableBaseProps extends Omit<ComponentProps<'s-table'>, 'paginate' | 'variant'> {
    headings: SIndexTableHeading[];
    pagination?: SIndexTablePagination;
    emptyState?: React.ReactNode;
    condensed?: boolean;
    resourceName?: SIndexTableResourceName;
    bulkActions?: SActionItem[];
    hideSelectAll?: boolean;
}
